import apiClient from '../api/apiClient';

/**
 * Basket Management Service
 * Handles API calls for Baskets and IFAs (for visibility selection)
 */
class BasketService {
  /**
   * Create a new basket
   * @param {Object} payload - { name, description, min_price, price_multiplier, items: [{ product_id, percentage }] }
   * @returns {Promise<{success: boolean, data: Object}>}
   */
  async createBasket(payload) {
    const response = await apiClient.post('/api/v1/admin/baskets', payload);
    return response.data;
  }

  /**
   * List baskets with pagination
   * @param {Object} params - { page, limit }
   * @returns {Promise<{success: boolean, data: { baskets, pagination }}>}
   */
  async listBaskets(params = {}) {
    const { signal, ...queryParams } = params;
    const response = await apiClient.get('/api/v1/admin/baskets', { params: queryParams, signal });
    return response.data;
  }

  /**
   * Get basket by ID (uuid)
   * @param {string} basketId - Basket UUID
   * @returns {Promise<{success: boolean, data: { basket, items, sector_weightage, assigned_ifas, risks, reasons }}>}
   */
  async getBasketById(basketId) {
    const response = await apiClient.get(`/api/v1/admin/baskets/${basketId}`);
    return response.data;
  }

  /**
   * Update basket visibility
   * @param {string} basketId - Basket UUID
   * @param {Object} payload - { visibility_type: 'none'|'all'|'selected', ifa_user_ids?: number[] }
   * @returns {Promise<{success: boolean}>}
   */
  async updateBasketVisibility(basketId, payload) {
    const response = await apiClient.put(`/api/v1/admin/baskets/${basketId}/visibility`, payload);
    return response.data;
  }

  /**
   * Update basket status (e.g. mark as INACTIVE)
   * @param {string} basketId - Basket UUID
   * @param {string} status - 'ACTIVE' | 'INACTIVE'
   */
  async updateBasketStatus(basketId, status) {
    const response = await apiClient.patch(`/api/v1/admin/baskets/${basketId}`, { status });
    return response.data;
  }

  /**
   * Add risks to a basket
   * @param {string} basketId - Basket UUID
   * @param {Array<{heading: string, description: string}>} risks
   */
  async addRisks(basketId, risks) {
    const response = await apiClient.post(`/api/v1/admin/baskets/${basketId}/risks`, { risks });
    return response.data;
  }

  /**
   * Add reasons to a basket
   * @param {string} basketId - Basket UUID
   * @param {Array<{heading: string, description: string}>} reasons
   */
  async addReasons(basketId, reasons) {
    const response = await apiClient.post(`/api/v1/admin/baskets/${basketId}/reasons`, { reasons });
    return response.data;
  }

  /**
   * List Partners for visibility selection
   * @param {Object} params - { status, page, limit, search, sort_by, sort_order, ... }
   * @returns {Promise<{success: boolean, data: { users, pagination }}>}
   */
  async listIfas(params = {}) {
    const defaultParams = {
      status: 'active',
      sort_by: 'created_at',
      sort_order: 'desc',
      page: 1,
      limit: 100,
      ...params,
    };
    const response = await apiClient.get('/api/v1/admin/partners', { params: defaultParams });
    return response.data;
  }
}

const basketService = new BasketService();
export default basketService;
